import { FileKind } from '../types/file-viewer.types';

export function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function downloadFile(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  downloadFile(objectUrl, filename);
  URL.revokeObjectURL(objectUrl);
}

export function isDirectDownloadUrl(url: string): boolean {
  return /^(blob:|data:)/i.test(url);
}

export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) {
    return null;
  }

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    return safeDecodeURIComponent(utf8Match[1]);
  }

  const quotedMatch = /filename="([^"]*)"/i.exec(header);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const unquotedMatch = /filename=([^;]+)/i.exec(header);
  return unquotedMatch?.[1]?.trim() ?? null;
}

export function extensionFromPath(path: string): string {
  const filename = filenameFromPath(path).toLowerCase();
  const lastDotIndex = filename.lastIndexOf('.');

  if (lastDotIndex === -1) {
    return '';
  }

  return filename.slice(lastDotIndex + 1);
}

export function filenameFromPath(path: string): string {
  const cleanedPath = path.split('?')[0].split('#')[0];
  const parts = cleanedPath.split('/');
  return parts[parts.length - 1] || '';
}

export function isMobileDevice(): boolean {
  const navigatorData = navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
  };

  if (navigatorData.userAgentData?.mobile != null) {
    return navigatorData.userAgentData.mobile;
  }

  const userAgent = navigator.userAgent || '';
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    return true;
  }

  // iPadOS can expose itself as a desktop Mac in Safari.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}


export function openFileInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function toCssDimension(value: number | string | null | undefined): string | null {
  if (value == null || value === '') {
    return null;
  }

  return typeof value === 'number' ? `${value}px` : value;
}

export function resolveFileKind(mimeType: string | null, extension: string): FileKind {
  const normalizedMimeType = mimeType?.toLowerCase() || '';

  if (
    normalizedMimeType.startsWith('image/') ||
    [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'bmp',
      'webp',
      'svg',
      'avif',
      'heic',
    ].includes(extension)
  ) {
    return 'image';
  }

  if (normalizedMimeType === 'application/pdf' || extension === 'pdf') {
    return 'pdf';
  }

  if (
    normalizedMimeType.includes('word') ||
    ['doc', 'docx', 'odt', 'rtf'].includes(extension)
  ) {
    return 'word';
  }

  if (
    normalizedMimeType.includes('excel') ||
    normalizedMimeType.includes('spreadsheet') ||
    ['xls', 'xlsx', 'csv', 'ods'].includes(extension)
  ) {
    return 'excel';
  }

  if (
    normalizedMimeType.includes('presentation') ||
    ['ppt', 'pptx', 'odp'].includes(extension)
  ) {
    return 'powerpoint';
  }

  if (
    ['zip', 'rar', '7z', 'tar', 'gz'].includes(extension) ||
    normalizedMimeType.includes('zip') ||
    normalizedMimeType.includes('compressed')
  ) {
    return 'archive';
  }

  if (
    normalizedMimeType.startsWith('video/') ||
    ['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(extension)
  ) {
    return 'video';
  }

  if (
    normalizedMimeType.startsWith('audio/') ||
    ['mp3', 'wav', 'ogg', 'm4a'].includes(extension)
  ) {
    return 'audio';
  }

  if (
    normalizedMimeType.startsWith('text/') ||
    ['txt', 'json', 'xml', 'log', 'md'].includes(extension)
  ) {
    return 'text';
  }

  return 'file';
}

export function resolveLanguageCode(languageCode: string | null | undefined): string {
  const normalizedCode = languageCode?.trim().toLowerCase();

  if (!normalizedCode) {
    return 'it';
  }

  return normalizedCode.split('-')[0];
}

export function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
